import React, { useContext } from 'react';
import {
  CopyUrlButton,
  SocialBar,
  useTPAComponentsContext,
} from 'wix-ui-tpa/cssVars';
import {
  Facebook,
  XCorp,
  LinkedIn,
  Link,
} from '@wix/wix-ui-icons-common/on-stage';
import settingsParams from '../../settingsParams';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { AppContext } from '../SettingsProvider/SettingsProvider';
import { ISettingsContextValue } from '@wix/tpa-settings';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { st, classes } from './SocialBar.st.css';
import {
  faqButtonClickSrc63Evid836,
  faqShareQuestion,
} from '@wix/bi-logger-labs-data/v2';
import { SOCIAL_URLS, SocialMedia } from '../../../../constants/social';
import { DATA_HOOKS } from '../../../../constants/dataHooksElements';
import stylesParams from '../../stylesParams';

const SocialWrapper: React.FC<{ questionId: string }> = ({ questionId }) => {
  const logger = useBi();
  const settings: ISettingsContextValue = useSettings();
  const styles = useStyles();
  const { baseURL } = useContext(AppContext);
  const env = useEnvironment();
  const { t } = useTranslation();
  const { appDefinitionId } = env;
  const isEditor = env.isEditor || env.isPreview;
  const { seo } = useTPAComponentsContext();

  // Get social settings
  const { facebook, twitter, linkedin, copyQuestionLink, show } = settings.get(
    settingsParams.social,
  );
  const showFacebookStyleParam =
    styles.get?.(stylesParams.showFacebookIcon) ?? true;
  const showTwitterStyleParam =
    styles.get?.(stylesParams.showTwitterIcon) ?? true;
  const showLinkedinStyleParam =
    styles.get?.(stylesParams.showLinkedinIcon) ?? true;
  const showCopyQuestionLinkStyleParam =
    styles.get?.(stylesParams.showCopyQuestionLink) ?? true;
  const showFacebook = facebook && showFacebookStyleParam;
  const showTwitter = twitter && showTwitterStyleParam;
  const showLinkedin = linkedin && showLinkedinStyleParam;
  const showCopyQuestionLink =
    copyQuestionLink && showCopyQuestionLinkStyleParam;
  const showSocial =
    showFacebook || showTwitter || showLinkedin || showCopyQuestionLink;
  const showSocialStyleParam =
    styles.get?.(stylesParams.showSocialIcons) ?? true;
  const showSocialBar = show && showSocial && showSocialStyleParam;
  if (!showSocialBar) {
    return null;
  }

  const socialShare = (
    media: SocialMedia,
    linkOnly: boolean = false,
    event: unknown | null,
  ): string | undefined => {
    const shareUrl = new URL(baseURL);
    shareUrl.searchParams.set('questionId', questionId);
    shareUrl.searchParams.set('appDefId', appDefinitionId);
    shareUrl.hash = questionId;
    if (event) {
      logger.report(
        faqShareQuestion({
          type: media,
          isOoi: true,
        }),
      );
    }
    if (media === SocialMedia.Copy) {
      return shareUrl.toString();
    }

    const url = SOCIAL_URLS[media] + encodeURIComponent(shareUrl.toString());
    if (!(seo || linkOnly)) {
      window.open(url, '_blank', 'top=200, left=200, width=700, height=500');
      logger.report(
        faqButtonClickSrc63Evid836({ button_name: media, action: 'link' }),
      );
    }
    return seo ? url : undefined;
  };

  return (
    <div
      className={st(classes.socialBarWrapper)}
      data-hook={DATA_HOOKS.WIDGET_SOCIAL_WRAPPER}
    >
      <SocialBar
        className={st(classes.socialBar)}
        data-hook={DATA_HOOKS.WIDGET_SOCIAL_BAR}
      >
        {showFacebook && (
          <SocialBar.Icon
            id={`${questionId}-facebook`}
            aria-label={t('display.tab.social.facebook.ariaLabel')}
            role="button"
            tooltip={
              isEditor
                ? t('social.icons.disablePreviewMode')
                : t('display.tab.social.facebook')
            }
            icon={<Facebook aria-labelledby={`${questionId}-facebook`} />}
            onClick={(event) => socialShare(SocialMedia.Facebook, false, event)}
            data-hook={DATA_HOOKS.WIDGET_SOCIAL_BAR_FACEBOOK}
            href={socialShare(SocialMedia.Facebook, true, null)}
            rel="noopener noreferrer"
          />
        )}

        {showTwitter && (
          <SocialBar.Icon
            aria-label={t('display.tab.social.twitter.ariaLabel')}
            id={`${questionId}-twitter`}
            role="button"
            tooltip={
              isEditor
                ? t('social.icons.disablePreviewMode')
                : t('display.tab.social.twitter')
            }
            icon={<XCorp aria-labelledby={`${questionId}-twitter`} />}
            onClick={(event) => socialShare(SocialMedia.Twitter, false, event)}
            data-hook={DATA_HOOKS.WIDGET_SOCIAL_BAR_TWITTER}
            href={socialShare(SocialMedia.Twitter, true, null)}
            rel="noopener noreferrer"
          />
        )}

        {showLinkedin && (
          <SocialBar.Icon
            id={`${questionId}-linkedin`}
            aria-label={t('display.tab.social.linkedin.ariaLabel')}
            role="button"
            tooltip={
              isEditor
                ? t('social.icons.disablePreviewMode')
                : t('display.tab.social.linkedin')
            }
            icon={<LinkedIn aria-labelledby={`${questionId}-linkedin`} />}
            onClick={(event) => socialShare(SocialMedia.LinkedIn, false, event)}
            data-hook={DATA_HOOKS.WIDGET_SOCIAL_BAR_LINKEDIN}
            href={socialShare(SocialMedia.LinkedIn, true, null)}
            rel="noopener noreferrer"
          />
        )}

        {showCopyQuestionLink && (
          <CopyUrlButton
            onClick={(event) => socialShare(SocialMedia.Copy, false, event)}
            id={`${questionId}-copy`}
            icon={<Link aria-labelledby={`${questionId}-copy`} />}
            aria-label={t('display.tab.social.copyQuestionLink')}
            role="button"
            tooltipText={
              isEditor
                ? t('social.icons.disablePreviewMode')
                : t('display.tab.social.copyQuestionLink')
            }
            successText={t('display.tab.social.copiedQuestionLink')}
            url={socialShare(SocialMedia.Copy, false, null)!}
            data-hook={DATA_HOOKS.WIDGET_SOCIAL_BAR_COPY}
            className={st(classes.copyButton)}
            href={socialShare(SocialMedia.Copy, true, null)}
            rel="noopener noreferrer"
          />
        )}
      </SocialBar>
    </div>
  );
};

export default SocialWrapper;
